import Lesson from '@/api/lesson';
import Module from '@/api/module';
import { createMediaField, safeLock } from '../common/util';

import { InternationalizedField } from '@/api/common';
import OutcomeFeedback from '@/api/outcomeFeedback';
import Video from '@/api/video';
import Exercise from '@/api/exercise';
import { copyExercise } from '@/util/copy-content/copy-exercise';
import Submodule from '@/api/submodule';
import { clone, cloneDeep, first, isEmpty, isNil } from 'lodash';

const copyLessonDetail = async (
  lesson: Lesson,
  targetModule: number,
  targetSubmodule: number
) => {
  const newLesson = new Lesson();
  newLesson.name = clone(lesson.name);
  newLesson.title = clone(lesson.title);
  newLesson.game_type = lesson.game_type;
  newLesson.description = clone(lesson.description);
  newLesson.parent_module = targetModule;
  newLesson.parent_submodule = targetSubmodule;
  newLesson.access = lesson.access;
  newLesson.difficulty_level = lesson.difficulty_level;
  newLesson.first_exercise_title = lesson.first_exercise_title;
  newLesson.game_subtype = lesson.game_subtype;
  newLesson.phoneme = lesson.phoneme;
  newLesson.reading_text = lesson.reading_text;
  newLesson.recommendable = lesson.recommendable;
  newLesson.reference_score = lesson.reference_score;
  newLesson.transcription = lesson.transcription;
  newLesson.version = lesson.version;
  newLesson.zoom_call = lesson.zoom_call;
  newLesson.metric_names = cloneDeep(lesson.metric_names);
  newLesson.version = lesson.version;

  await newLesson.save();
  return newLesson;
};

const copyOutcomeFeedBack = async (
  outcomeFeedback: OutcomeFeedback,
  parentLesson: number
) => {
  if (isNil(outcomeFeedback)) {
    return null;
  }

  const newOutcomeFeedback = new OutcomeFeedback();
  newOutcomeFeedback.parent_lesson = parentLesson;
  newOutcomeFeedback.threshold = outcomeFeedback.threshold;
  newOutcomeFeedback.positive_feedback_i18n =
    outcomeFeedback.positive_feedback_i18n;
  newOutcomeFeedback.negative_feedback_i18n =
    outcomeFeedback.negative_feedback_i18n;

  await newOutcomeFeedback.save();

  if (!isEmpty(outcomeFeedback.positive_image_link)) {
    newOutcomeFeedback.positive_image_path = await createMediaField(
      outcomeFeedback.positive_image_link,
      newOutcomeFeedback.id,
      'outcomefeedback',
      'positive_image_path',
      'image'
    );
  }

  if (!isEmpty(outcomeFeedback.negative_image_link)) {
    newOutcomeFeedback.negative_image_path = await createMediaField(
      outcomeFeedback.negative_image_link,
      newOutcomeFeedback.id,
      'outcomefeedback',
      'negative_image_path',
      'image'
    );
  }

  await newOutcomeFeedback.save();
  return newOutcomeFeedback;
};

export const copyLessonVideo = async (
  lessonVideos: Video[],
  parentLesson: number
) => {
  if (isEmpty(lessonVideos)) {
    return [];
  }

  const videos = lessonVideos.map((item) => {
    const newVideo = new Video();
    newVideo.parent_id = parentLesson;
    newVideo.parent_model = Lesson.SLUG;
    newVideo.interface_elements = item.interface_elements;
    newVideo.meta = item.meta;
    newVideo.mime = item.mime;
    newVideo.path = item.path;
    newVideo.promoted = item.promoted;
    newVideo.public_url = item.public_url;
    newVideo.public_url_expiration = item.public_url_expiration;
    newVideo.start_time = item.start_time;
    newVideo.end_time = item.end_time;
    newVideo.title = item.title;
    newVideo.url = item.url;
    newVideo.thumbnail_url = item.thumbnail_url;
    newVideo.tags = item.tags;
    newVideo.type = item.type;
    newVideo.display_condition = item.display_condition;
    newVideo.description = item.description;
    newVideo.order = item.order;

    return newVideo;
  });

  const jobs = videos.map((item) => item.save());
  await Promise.all(jobs);

  return videos;
};

export const copyLesson = async (
  lesson: Lesson,
  targetModule: number,
  targetSubmodule?: number
) => {
  const newLesson = await safeLock(Module.SLUG, targetModule, async () => {
    let newLesson: Lesson;

    if (isNil(targetSubmodule)) {
      newLesson = await copyLessonDetail(lesson, targetModule, targetSubmodule);
    } else {
      newLesson = await safeLock(Submodule.SLUG, targetSubmodule, async () => {
        return copyLessonDetail(lesson, targetModule, targetSubmodule);
      });
    }

    return newLesson;
  });

  if (isNil(newLesson)) {
    return;
  }

  await safeLock(Lesson.SLUG, newLesson.id, async () => {
    newLesson.outcome_feedback = await copyOutcomeFeedBack(
      lesson.outcome_feedback,
      newLesson.id
    );

    const videos = await copyLessonVideo(lesson.video_objects, newLesson.id);
    newLesson.video_objects = videos;
    if (!isEmpty(videos)) {
      newLesson.video = first(videos).id;
    }

    if (!isEmpty(lesson.image_link)) {
      newLesson.image_path = await createMediaField(
        lesson.image_link,
        newLesson.id,
        Lesson.SLUG,
        'image_path',
        'image'
      );
    }

    if (!isEmpty(lesson.bg_image_link)) {
      newLesson.bg_image_path = await createMediaField(
        lesson.bg_image_link,
        newLesson.id,
        Lesson.SLUG,
        'bg_image_path',
        'image'
      );
    }

    await newLesson.save();
  });

  const exercises = await Exercise.getExercisesOfLesson(lesson.id);

  for (const item of exercises.results) {
    const exerciseDetail = await Exercise.get(item.id);
    console.log(exerciseDetail);
    await copyExercise(exerciseDetail, newLesson.id);
  }
};
